import React from 'react';
import { Palette } from 'lucide-react';
import { DirectiveSection } from './DirectiveSection';
import { SharedPatternsSettings } from '@/components/settings/SharedPatternsSettings';

export const DesignDirectivesTab: React.FC = () => {
  return (
    <div className="h-full overflow-y-auto p-4 space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <Palette className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Design System Directives</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-4">
        Configure design tokens that agents will follow when generating visual content.
        These directives override agent default colors, typography, and spacing.
      </p>

      <DirectiveSection
        title="Brand Colors"
        description="Primary, secondary, and accent colors for generated content."
        aspectFilter="design-system-colors"
        template={[
          { key: 'Primary Color', label: 'Primary', type: 'color', placeholder: '#2D5A3D' },
          { key: 'Primary Light', label: 'Primary Light', type: 'color', placeholder: '#4A7C5C' },
          { key: 'Primary Dark', label: 'Primary Dark', type: 'color', placeholder: '#1A3D28' },
          { key: 'Accent Color', label: 'Accent', type: 'color', placeholder: '#3B82F6' },
          { key: 'Background', label: 'Background', type: 'color', placeholder: '#F9FAFB' },
          { key: 'Card Background', label: 'Card Background', type: 'color', placeholder: '#ffffff' },
        ]}
      />

      <DirectiveSection
        title="Typography"
        description="Font family, size scale, and weight preferences."
        aspectFilter="design-system-typography"
        template={[
          { key: 'Font Family', label: 'Font Family', type: 'text', placeholder: 'Inter, sans-serif' },
          { key: 'Heading Size', label: 'Heading Size (px)', type: 'number', placeholder: '28' },
          { key: 'Body Size', label: 'Body Size (px)', type: 'number', placeholder: '16' },
          { key: 'Caption Size', label: 'Caption Size (px)', type: 'number', placeholder: '12' },
        ]}
      />

      <DirectiveSection
        title="Spacing & Layout"
        description="Grid unit, padding, and gap preferences."
        aspectFilter="design-system-spacing"
        template={[
          { key: 'Grid Unit', label: 'Grid Unit (px)', type: 'number', placeholder: '8' },
          { key: 'Card Padding', label: 'Card Padding (px)', type: 'number', placeholder: '24' },
          { key: 'Section Gap', label: 'Section Gap (px)', type: 'number', placeholder: '24' },
          { key: 'Border Radius', label: 'Border Radius (px)', type: 'number', placeholder: '12' },
        ]}
      />

      <DirectiveSection
        title="Custom Rules"
        description="Free-form design rules that agents must follow (e.g. 'never use drop shadows', 'always use dark mode')."
        aspectFilter="design-system-rules"
        freeForm
      />

      <SharedPatternsSettings isAdmin={true} />
    </div>
  );
};
